/**
 * Kunden (CRM-leicht, Stitch: maestro_kunden + maestro_kunden_profil):
 * Filter-Chips + Suchleiste + 7-Spalten-Tabelle; Zeile navigiert zur Vollseite
 * pages/customer-detail.tsx (H4 — vorher Drawer). "Kunde anlegen"-Drawer bleibt hier.
 */
import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useApi, apiMutate } from "../hooks/useApi";
import { useContentRefetch } from "../ui/CopilotProvider";
import { currentMembership } from "../lib/membership";
import type { Customer, EventRow, Me } from "../ui/types";
import { formatDate, money } from "../ui/format";
import { useListSort, type SortSpalte } from "../hooks/useListSort";
import { useUrlZustand } from "../hooks/useUrlZustand";
import { useNarrowViewport } from "../hooks/useNarrowViewport";
import { oeffneGlobaleSuche } from "../lib/globale-suche";
import { kundentypVon, type Kundentyp } from "../ui/Kundentyp";
import {
  Button,
  Card,
  Drawer,
  EmptyState,
  ErrorNote,
  Icon,
  Input,
  KeineAngabe,
  ListFooter,
  ListenReiter,
  LoadingRows,
  Modal,
  PageHeader,
  SortierKopf,
  SortierMenue,
  ZeilenLink,
  Checkbox,
  ButtonRouterLink,
} from "../ui/components";

/**
 * Dedup-Plan Phase 4 (30.08.2026) — ein Kandidatenpaar aus `GET /api/customers/duplicates`.
 * `gruende`: 'telefon' (gleiche Normalform, lib/telefon.ts/sql 340) und/oder 'name' (exakt,
 * case-insensitiv, getrimmt). Bewusst nur diese praezisen Signale, keine Fuzzy-Aehnlichkeit.
 * Seit 11.09.2026 zusaetzlich 'anfrage_uebernahme' (sql/410, KONZEPT-ANFRAGE-VORGANG-
 * VEREINHEITLICHUNG Stufe 3b): das System selbst hat erkannt, dass ein sofort bei
 * Anfrage-Eingang angelegter Kunde bei `komponieren`/`manuell-uebernehmen` durch eine explizite
 * Uebernahme eines anderen Kunden abgeloest wurde.
 */
export interface DuplicateSide {
  id: string;
  name: string;
  company: string | null;
  email: string | null;
  phone: string | null;
  createdAt: string;
  events: number;
}
export interface DuplicatePair {
  gruende: string[];
  a: DuplicateSide;
  b: DuplicateSide;
}

function initials(name: string): string {
  const parts = name.split(/\s+/).filter(Boolean).slice(0, 2);
  const out = parts.map((p) => p[0]?.toUpperCase() ?? "").join("");
  return out || "?";
}

/** Stabiler Schlüssel eines Dubletten-Paars — a=Ziel/b=Quelle (die bestehende Konvention der
 *  Liste, siehe Kopfkommentar von `DuplicatePair`), unveraendert uebernommen fuer Auswahl UND
 *  Sammel-Bestaetigung. */
function dupKey(p: DuplicatePair): string {
  return `${p.a.id}:${p.b.id}`;
}

/** Punkt 42 (Migration 533): der Kundentyp ist jetzt ein Feld am Kunden (ui/Kundentyp.tsx). */
type CustomerType = Kundentyp;

const OPEN_EVENT_STATUS = new Set(["offer_sent", "offer_chosen", "payment_failed", "balance_due"]);
const LOST_EVENT_STATUS = new Set(["cancelled", "offer_declined", "no_response"]);

/** Listenstandard (Punkt 95): je Kunde einmal abgeleitete Werte — Zelle, Sortierung und Fußzeile
 *  lesen dieselben Zahlen. */
interface KundenZeile {
  c: Customer;
  events: number;
  /** Umsatz = bezahlte + durchgeführte Vorgänge (wie bisher in der Zelle). */
  revenue: number;
  openCents: number;
  isMerged: boolean;
  isLost: boolean;
  lastContact: string;
  type: CustomerType;
}

/** Zelle einer Kunden-Zeile (Brief § 2.3, gleiche Maße wie die Buchungen-Liste). */
const ZELLE =
  "border-t border-[#F6F0E4] px-[7px] py-3 align-middle first:pl-[18px] last:pr-[18px] dark:border-white/[0.06]";

/** Listenstandard (Brief § 3/§ 9): alle sieben Spalten sortierbar. Sortiert wird, was die Zelle
 *  zeigt — ohne Firma (Zelle „Privat“) und ohne bezahlten Umsatz (Zelle „–“ bzw. nur „… offen“)
 *  gilt der Wert als leer und steht immer am Ende. Offene Beträge stehen klein darunter, zählen
 *  für die Sortierung aber nicht (Antoine 23.09.2026). */
const KUNDEN_SPALTEN: SortSpalte<KundenZeile>[] = [
  { key: "kunde", label: "Kunde", type: "text", accessor: (z) => z.c.name },
  { key: "firma", label: "Firma / Typ", type: "text", accessor: (z) => z.c.company },
  { key: "email", label: "E-Mail", type: "text", accessor: (z) => z.c.email },
  { key: "telefon", label: "Telefon", type: "text", accessor: (z) => z.c.phone },
  { key: "events", label: "Events", type: "number", accessor: (z) => z.events },
  { key: "umsatz", label: "Umsatz", type: "money", accessor: (z) => (z.revenue > 0 ? z.revenue : null) },
  { key: "kontakt", label: "Letzter Kontakt", type: "date", accessor: (z) => z.lastContact },
];
const RECHTS = new Set(["events", "umsatz", "kontakt"]);
/** Handy-Karten (Handy-Mockup 26.09.2026): Sortierung als Menü, gleiche Schlüssel wie die Spalten. */
const KUNDEN_SORTIERUNG = [
  { label: "Standard", key: null },
  { label: "Kontakt", key: "kontakt", dir: "desc" },
  { label: "Name A–Z", key: "kunde", dir: "asc" },
  { label: "Umsatz ↓", key: "umsatz", dir: "desc" },
  { label: "Events ↓", key: "events", dir: "desc" },
] as const;
const TYP_LABEL: Record<CustomerType, string> = { privat: "Privat", firma: "Firma", verein: "Verein" };

export default function CustomersPage() {
  const { data, loading, error, reload } = useApi<Customer[]>("/api/customers");
  // Chat-P1: create_customer/update_customer per Chat -> Liste live nachladen.
  useContentRefetch("customer", reload);
  const eventsQ = useApi<EventRow[]>("/api/events");
  // Dedup-Plan Phase 4: das Dubletten-Review ist eine Owner/Admin-Flaeche (dieselbe Grenze wie
  // der Merge, PR #807) — Staff sieht die Sektion gar nicht erst, die Routen erzwingen es auch.
  const meQ = useApi<Me>("/api/me");
  const membership = currentMembership(meQ.data);
  const istInhaber = membership?.role === "owner";
  const dupQ = useApi<DuplicatePair[]>(istInhaber ? "/api/customers/duplicates" : null);
  const navigate = useNavigate();
  const [createOpen, setCreateOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  // Punkt 115 (Paket 3): Suche und Typfilter in der Adresse — nach „Zurück“ vom Kunden wieder da.
  const [zustand, setzeZustand] = useUrlZustand({ q: "", typ: "all", erkannt: "" });
  // Punkt 42: nur „automatisch erkannte“ Typen zeigen — so lassen sich z. B. Vereine gezielt nachsehen.
  const nurErkannt = zustand.erkannt === "1";
  const search = zustand.q;
  const setSearch = (q: string) => setzeZustand({ q });
  const typeFilter: "all" | CustomerType = (["privat", "firma", "verein"] as const).find((t) => t === zustand.typ) ?? "all";
  const setTypeFilter = (typ: "all" | CustomerType) => setzeZustand({ typ });
  // Handy-Mockup 26.09.2026 (Frage 4): Dubletten mobil in eigener Ansicht (`?ansicht=dubletten`),
  // vor der Kundenliste nur eine Zeile. Push statt replace, damit „Zurück“ zur Liste führt.
  const narrow = useNarrowViewport();
  const [ansichtParams] = useSearchParams();
  const dublettenAnsicht = narrow && ansichtParams.get("ansicht") === "dubletten";

  const customers = data ?? [];
  const events = eventsQ.data ?? [];
  // Perf: pro Zeile/Summe wurde bisher `events.filter(e => e.customerId === c.id)` aufgerufen -
  // O(Kunden * Events). Bei den ueber viele E2E-/Testlaeufe angesammelten Datensaetzen der
  // geteilten Neon-CI-Test-Branch (Stand 18.08.2026: 4071 Kunden, 9676 Events) hat das den
  // Render nach jedem "Kunde anlegen" so weit verlangsamt, dass eine neue Zeile nicht mehr
  // innerhalb von 15s sichtbar wurde (kunden-historie.spec.ts). Eine einmalige Map pro
  // `events`-Aenderung macht daraus O(Kunden + Events).
  const eventsByCustomer = useMemo(() => {
    const map = new Map<string, EventRow[]>();
    for (const e of events) {
      const list = map.get(e.customerId);
      if (list) list.push(e);
      else map.set(e.customerId, [e]);
    }
    return map;
  }, [events]);
  const zeilen = useMemo<KundenZeile[]>(
    () =>
      customers.map((c) => {
        const evs = eventsByCustomer.get(c.id) ?? [];
        // H3: verlorener Kunde ausgegraut, wenn alle bisherigen Vorgaenge im Sand verliefen.
        // Dedup-Plan Phase 1: ein Grabstein (mergedIntoId gesetzt) bleibt AUFFINDBAR, wird aber
        // wie ein verlorener Kunde gedimmt und traegt ein eigenes Abzeichen — dieselbe Behandlung,
        // die anonymisierte Kunden implizit bekommen (Platzhalter-Namen in derselben Liste).
        const isMerged = Boolean(c.mergedIntoId);
        return {
          c,
          events: evs.length,
          revenue: evs.filter((e) => ["paid", "completed"].includes(e.status)).reduce((s, e) => s + (e.amountTotalCents ?? 0), 0),
          openCents: evs.filter((e) => OPEN_EVENT_STATUS.has(e.status)).reduce((s, e) => s + (e.amountTotalCents ?? 0), 0),
          isMerged,
          isLost: isMerged || (evs.length > 0 && evs.every((e) => LOST_EVENT_STATUS.has(e.status))),
          lastContact: [c.createdAt, ...evs.map((e) => e.createdAt)].sort().at(-1) ?? c.createdAt,
          type: kundentypVon(c),
        };
      }),
    [customers, eventsByCustomer],
  );
  /** Punkt 115 (G1): die Suche wirkt VOR dem Typfilter — die Chips zählen die Suchtreffer. */
  const suchTreffer = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return zeilen;
    return zeilen.filter(
      ({ c }) =>
        c.name.toLowerCase().includes(q) ||
        (c.email ?? "").toLowerCase().includes(q) ||
        (c.company ?? "").toLowerCase().includes(q),
    );
  }, [zeilen, search]);
  const typeCounts = useMemo(() => {
    const counts = { privat: 0, firma: 0, verein: 0, erkannt: 0 };
    for (const z of suchTreffer) {
      counts[z.type]++;
      if (z.c.kundentypAutomatisch) counts.erkannt++;
    }
    return counts;
  }, [suchTreffer]);
  const filtered = useMemo(
    () =>
      suchTreffer.filter((z) => (typeFilter === "all" || z.type === typeFilter) && (!nurErkannt || z.c.kundentypAutomatisch)),
    [suchTreffer, typeFilter, nurErkannt],
  );
  const sort = useListSort("kunden", KUNDEN_SPALTEN);
  // ponytail: keine Virtualisierung (Plan § Grundbefund); nachrüsten, wenn die Liste > ~200 Zeilen wächst.
  const sortiert = sort.sortiere(filtered);
  // Fußzeile (Brief § 2.5): Summe des Umsatzes der SICHTBAREN Zeilen.
  const sichtbarerUmsatzCents = filtered.reduce((s, z) => s + z.revenue, 0);

  const lifetimeSumAllCents = useMemo(() => zeilen.reduce((s, z) => s + z.revenue, 0), [zeilen]);
  const topCustomer = useMemo(() => {
    let best: { name: string; revenue: number } | null = null;
    for (const { c, revenue } of zeilen) {
      if (!best || revenue > best.revenue) best = { name: c.company || c.name, revenue };
    }
    return best;
  }, [zeilen]);

  /**
   * Dedup-Plan Phase 4: "Verschiedene Kunden" — merkt das Paar dauerhaft (customer_distinct_pairs)
   * und laedt das Review neu. Kein confirm-Dialog: die Markierung legt nichts zusammen und
   * loescht nichts, sie nimmt dem Review nur ein Paar weg.
   */
  const [dismissBusyKey, setDismissBusyKey] = useState<string | null>(null);
  const [dupActionError, setDupActionError] = useState<string | null>(null);
  async function dismissPair(p: DuplicatePair) {
    const key = dupKey(p);
    setDismissBusyKey(key);
    setDupActionError(null);
    try {
      await apiMutate("/api/customers/duplicates/dismiss", "POST", { customerAId: p.a.id, customerBId: p.b.id });
      dupQ.reload();
    } catch (err) {
      setDupActionError(err instanceof Error ? err.message : String(err));
    } finally {
      setDismissBusyKey(null);
    }
  }

  /**
   * "Mehrfachauswahl mit Kurzbestätigung" (Antoine 31.08.2026): Checkboxen direkt in der Liste,
   * EIN Bestätigungsdialog für alle ausgewählten Paare statt Seitenwechsel je Paar. Der Merge
   * selbst bleibt der bestehende `POST /api/customers/:id/merge` (kein neuer Batch-Endpunkt) —
   * das Frontend ruft ihn nach der Bestätigung sequenziell je Paar auf (`bulkOffen`-Dialog unten).
   */
  const [ausgewaehlteDubletten, setAusgewaehlteDubletten] = useState<Set<string>>(new Set());
  const [bulkOffen, setBulkOffen] = useState(false);
  // Verschwindet ein ausgewähltes Paar aus der Liste (zusammengeführt/verworfen/vom Server nicht
  // mehr geliefert), muss die Auswahl das nachvollziehen — sonst zählt die Aktionsleiste Paare
  // mit, die es gar nicht mehr gibt (gleiches Muster wie `gewaehlt` in freigaben.tsx).
  useEffect(() => {
    if (!dupQ.data) return;
    const lebendeSchluessel = new Set(dupQ.data.map(dupKey));
    setAusgewaehlteDubletten((prev) => {
      let veraendert = false;
      const next = new Set<string>();
      for (const k of prev) {
        if (lebendeSchluessel.has(k)) next.add(k);
        else veraendert = true;
      }
      return veraendert ? next : prev;
    });
  }, [dupQ.data]);
  function toggleDublette(p: DuplicatePair) {
    setAusgewaehlteDubletten((prev) => {
      const next = new Set(prev);
      const k = dupKey(p);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });
  }
  const alleDublettenAusgewaehlt = (dupQ.data?.length ?? 0) > 0 && (dupQ.data ?? []).every((p) => ausgewaehlteDubletten.has(dupKey(p)));
  function toggleAlleDubletten() {
    if (alleDublettenAusgewaehlt) {
      setAusgewaehlteDubletten(new Set());
    } else {
      setAusgewaehlteDubletten(new Set((dupQ.data ?? []).map(dupKey)));
    }
  }
  const ausgewaehltePaare = (dupQ.data ?? []).filter((p) => ausgewaehlteDubletten.has(dupKey(p)));

  async function handleCreate(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    setBusy(true);
    setFormError(null);
    try {
      await apiMutate("/api/customers", "POST", {
        name: form.get("name"),
        email: form.get("email"),
        company: (form.get("company") as string) || undefined,
        phone: (form.get("phone") as string) || undefined,
      });
      setCreateOpen(false);
      reload();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <PageHeader
        title="Kunden"
        subtitle={customers.length > 0 ? `${customers.length} Kunden · ${money(lifetimeSumAllCents)} Lifetime-Umsatz` : "Kontakte, Historie und Umsätze Ihrer Kunden."}
        actions={
          narrow ? (
            // Handy: „Kunde anlegen“ als Symbolknopf; Adressen/Vormerkungen stehen als Zeilen darunter.
            <Button variant="primary" className="!px-2.5" aria-label="Kunde anlegen" onClick={() => setCreateOpen(true)}>
              <Icon name="person_add" className="text-[18px]" />
            </Button>
          ) : (
          <>
            {/* Adressmodell PR4 (WARTESCHLANGE Punkt 7): Owner/Admin-Aufraeumflaeche, dasselbe
                Rollen-Gate wie die Dubletten-Kandidaten unten. */}
            {istInhaber && (
              <ButtonRouterLink to="/kunden/adressen-ohne-land" variant="secondary">
                <Icon name="public_off" className="text-[18px]" /> Adressen ohne Land
              </ButtonRouterLink>
            )}
            {/* Punkt 101 Stufe 1: Saison-Vormerkungen der Website (nur lesen). */}
            <Button variant="secondary" onClick={() => navigate("/kunden/vormerkungen")}>
              <Icon name="event_upcoming" className="text-[18px]" /> Vormerkungen
            </Button>
            <Button variant="primary" onClick={() => setCreateOpen(true)}>
              <Icon name="person_add" className="text-[18px]" /> Kunde anlegen
            </Button>
          </>
          )
        }
      />

      {narrow && !dublettenAnsicht && (
        <Card className="mb-4 overflow-hidden">
          <ul className="divide-y divide-outline-variant/30 dark:divide-outline/30">
            {istInhaber && (dupQ.data?.length ?? 0) > 0 && (
              <li>
                <Link to="/kunden?ansicht=dubletten" data-testid="kunden-dubletten-zeile" className="flex items-center gap-3 px-4 py-3">
                  <Icon name="join_left" className="text-[20px] text-primary" />
                  <span className="min-w-0 flex-1">
                    <span className="block text-[13px] font-semibold text-on-background dark:text-inverse-on-surface">
                      {dupQ.data!.length.toLocaleString("de-DE")} mögliche Dubletten
                    </span>
                    <span className="block text-[11.5px] text-secondary dark:text-secondary-fixed-dim">
                      Gleiche Nummer oder gleicher Name · nichts wird automatisch zusammengelegt
                    </span>
                  </span>
                  <Icon name="chevron_right" className="text-[18px] text-on-surface-variant/60" />
                </Link>
              </li>
            )}
            {istInhaber && (
              <li>
                <Link to="/kunden/adressen-ohne-land" className="flex items-center gap-3 px-4 py-3 text-[13px] text-on-background dark:text-inverse-on-surface">
                  <Icon name="public_off" className="text-[18px] text-on-surface-variant" /> <span className="flex-1">Adressen ohne Land</span>
                  <Icon name="chevron_right" className="text-[18px] text-on-surface-variant/60" />
                </Link>
              </li>
            )}
            <li>
              <Link to="/kunden/vormerkungen" className="flex items-center gap-3 px-4 py-3 text-[13px] text-on-background dark:text-inverse-on-surface">
                <Icon name="event_upcoming" className="text-[18px] text-on-surface-variant" /> <span className="flex-1">Vormerkungen</span>
                <Icon name="chevron_right" className="text-[18px] text-on-surface-variant/60" />
              </Link>
            </li>
          </ul>
        </Card>
      )}
      {dublettenAnsicht && (
        <Link to="/kunden" className="mb-3 inline-flex items-center gap-1 text-[12.5px] font-semibold text-primary dark:text-primary-fixed">
          <Icon name="arrow_back" className="text-[16px]" /> Zur Kundenliste
        </Link>
      )}

      {/* Dedup-Plan Phase 4 (30.08.2026) + Mehrfachauswahl (31.08.2026): Bestands-Dubletten-Review
          — nur Owner/Admin. Kandidatenpaare aus GET /api/customers/duplicates (praezise Signale:
          gleiche Telefon-Normalform / exakt gleicher Name). Je Paar zwei Einzel-Aktionen:
          "Zusammenführen" oeffnet weiterhin den bestehenden Merge-Flow (customer-detail.tsx,
          ?merge=…) mit fertiger Gegenueberstellung; "Verschiedene Kunden" merkt das Paar
          dauerhaft als geklaert. NEU: eine Checkbox je Paar + "Alle auswählen" — bei ≥1 Auswahl
          erscheint die Aktionsleiste mit "Zusammenführen (N)", die EINEN Bestätigungsdialog für
          alle ausgewählten Paare öffnet (BulkZusammenfuehrenDialog unten). NIE automatisch: jede
          Zusammenführung bleibt eine explizite menschliche Bestätigung (Antoine 30./31.08.2026). */}
      {istInhaber && (!narrow || dublettenAnsicht) && (
        <Card className="mb-5 overflow-hidden">
          <div className="flex flex-wrap items-center gap-2.5 border-b border-outline-variant/30 px-4 py-3 dark:border-outline/30">
            <Icon name="join_left" className="text-[18px] text-primary" />
            <span className="font-body-md text-body-md font-semibold text-on-background dark:text-inverse-on-surface">
              Dubletten-Kandidaten
            </span>
            {(dupQ.data?.length ?? 0) > 0 && (
              <span className="rounded-full bg-primary/14 px-2 py-0.5 font-label-md text-[11px] font-bold text-primary">
                {dupQ.data!.length}
              </span>
            )}
            {(dupQ.data?.length ?? 0) > 0 && (
              <label className="flex items-center gap-1.5 font-body-sm text-[11.5px] font-medium text-on-surface-variant dark:text-secondary-fixed-dim">
                <Checkbox
                  aria-label="Alle auswählen"
                  checked={alleDublettenAusgewaehlt}
                  onChange={toggleAlleDubletten}
                />
                Alle auswählen
              </label>
            )}
            <span className="ml-auto font-body-sm text-[11px] text-on-surface-variant/70">
              Gleiche Telefonnummer oder exakt gleicher Name — nur Vorschläge, nichts wird automatisch zusammengelegt.
            </span>
          </div>
          {/* Aktionsleiste: erscheint erst ab einer Auswahl (GATE-Muster wie freigaben.tsx). */}
          {ausgewaehlteDubletten.size > 0 && (
            <div className="flex items-center gap-3 border-b border-outline-variant/30 bg-primary/5 px-4 py-2.5 dark:border-outline/30">
              <span className="font-body-sm text-[12.5px] font-semibold text-on-background dark:text-inverse-on-surface">
                {ausgewaehlteDubletten.size} ausgewählt
              </span>
              <Button variant="secondary" className="!py-1.5" onClick={() => setAusgewaehlteDubletten(new Set())}>
                Auswahl aufheben
              </Button>
              <Button variant="primary" className="!py-1.5 ml-auto" onClick={() => setBulkOffen(true)}>
                <Icon name="merge" className="text-[15px]" /> Zusammenführen ({ausgewaehlteDubletten.size})
              </Button>
            </div>
          )}
          {dupQ.error && <ErrorNote message={dupQ.error} onRetry={dupQ.reload} />}
          {dupActionError && <ErrorNote message={dupActionError} />}
          {dupQ.loading ? (
            <LoadingRows rows={2} />
          ) : (dupQ.data?.length ?? 0) === 0 ? (
            <p className="px-4 py-3 font-body-sm text-[12.5px] text-secondary-fixed-dim">Keine Dubletten-Kandidaten gefunden.</p>
          ) : (
            <ul className="divide-y divide-outline-variant/30 dark:divide-outline/30">
              {dupQ.data!.map((p) => {
                const key = dupKey(p);
                const seite = (k: DuplicateSide) => (
                  <div className="min-w-0 flex-1">
                    {/* Einheit E: war ein `<button onClick={navigate(...)}>` — ein Ziel ohne
                        Linkziel. Hier reicht ein gewoehnlicher `<Link>` (die Zeile selbst ist
                        KEIN Link: sie traegt Auswahl-Checkbox und zwei Aktionen und hat kein
                        eigenes Ziel), damit Cmd-/Strg-/Mittelklick auch hier funktionieren. */}
                    <Link
                      to={`/kunden/${k.id}`}
                      className={`block max-w-full ${narrow ? "" : "truncate "}font-body-sm text-[12.5px] font-semibold text-on-background hover:text-primary hover:underline dark:text-inverse-on-surface`}
                    >
                      {k.name}
                      {k.company ? ` · ${k.company}` : ""}
                    </Link>
                    <p className={`${narrow ? "break-words" : "truncate"} font-body-sm text-[11px] text-on-surface-variant`}>
                      {[k.email, k.phone, `${k.events} Vorgänge`].filter(Boolean).join(" · ")}
                    </p>
                  </div>
                );
                return (
                  narrow ? (
                    // Handy: je Paar untereinander, beide Namen voll, darunter die Aktionen nebeneinander.
                    <li key={key} className="space-y-2 px-4 py-3" data-testid="dubletten-paar">
                      <div className="flex items-start gap-3">
                        <Checkbox
                          aria-label={`${p.a.name} und ${p.b.name} auswählen`}
                          checked={ausgewaehlteDubletten.has(key)}
                          onChange={() => toggleDublette(p)}
                          className="mt-1 shrink-0"
                        />
                        <div className="min-w-0 flex-1 space-y-1.5">
                          {seite(p.a)}
                          {seite(p.b)}
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <Button variant="secondary" className="!py-1.5" onClick={() => navigate(`/kunden/${p.a.id}?merge=${p.b.id}`)}>
                          <Icon name="merge" className="text-[15px]" /> Zusammenführen
                        </Button>
                        <Button variant="secondary" className="!py-1.5" disabled={dismissBusyKey === key} onClick={() => void dismissPair(p)}>
                          <Icon name="person_off" className="text-[15px]" />
                          {dismissBusyKey === key ? "Speichert …" : "Verschieden"}
                        </Button>
                      </div>
                    </li>
                  ) : (
                  <li key={key} className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3">
                    <Checkbox
                      aria-label={`${p.a.name} und ${p.b.name} auswählen`}
                      checked={ausgewaehlteDubletten.has(key)}
                      onChange={() => toggleDublette(p)}
                      className="shrink-0"
                    />
                    {seite(p.a)}
                    <Icon name="sync_alt" className="shrink-0 text-[16px] text-on-surface-variant/60" />
                    {seite(p.b)}
                    <span className="flex shrink-0 items-center gap-1.5">
                      {p.gruende.includes("telefon") && (
                        <span className="rounded-full bg-surface-variant px-2 py-0.5 font-label-md text-[10px] font-semibold text-secondary dark:bg-white/[0.08] dark:text-secondary-fixed-dim">
                          Gleiche Telefonnummer
                        </span>
                      )}
                      {p.gruende.includes("name") && (
                        <span className="rounded-full bg-surface-variant px-2 py-0.5 font-label-md text-[10px] font-semibold text-secondary dark:bg-white/[0.08] dark:text-secondary-fixed-dim">
                          Gleicher Name
                        </span>
                      )}
                      {p.gruende.includes("anfrage_uebernahme") && (
                        <span className="rounded-full bg-surface-variant px-2 py-0.5 font-label-md text-[10px] font-semibold text-secondary dark:bg-white/[0.08] dark:text-secondary-fixed-dim">
                          Aus derselben Anfrage
                        </span>
                      )}
                    </span>
                    <span className="flex shrink-0 items-center gap-2">
                      <Button
                        variant="secondary"
                        className="!py-1.5"
                        title="Öffnet die Gegenüberstellung — zusammengelegt wird erst nach expliziter Bestätigung."
                        onClick={() => navigate(`/kunden/${p.a.id}?merge=${p.b.id}`)}
                      >
                        <Icon name="merge" className="text-[15px]" /> Zusammenführen
                      </Button>
                      <Button
                        variant="secondary"
                        className="!py-1.5"
                        disabled={dismissBusyKey === key}
                        title="Merkt dieses Paar dauerhaft als geklärt — es wird nicht erneut vorgeschlagen."
                        onClick={() => void dismissPair(p)}
                      >
                        <Icon name="person_off" className="text-[15px]" />
                        {dismissBusyKey === key ? "Speichert …" : "Verschiedene Kunden"}
                      </Button>
                    </span>
                  </li>
                  )
                );
              })}
            </ul>
          )}
        </Card>
      )}

      <BulkZusammenfuehrenDialog
        offen={bulkOffen}
        paare={ausgewaehltePaare}
        onClose={() => setBulkOffen(false)}
        onFertig={() => {
          setAusgewaehlteDubletten(new Set());
          dupQ.reload();
        }}
      />

      {/* H2: Filter-Chips Alle/Privat/Firmen/Vereine; auf dem Handy eine Reiterleiste in der Karte. */}
      {!dublettenAnsicht && (
      <>
      {!narrow && (
      <div className="mb-4 flex flex-wrap gap-2">
        {(
          [
            { key: "all", label: "Alle", count: suchTreffer.length },
            { key: "privat", label: "Privat", count: typeCounts.privat },
            { key: "firma", label: "Firmen", count: typeCounts.firma },
            { key: "verein", label: "Vereine", count: typeCounts.verein },
          ] as const
        ).map((f) => (
          <button
            key={f.key}
            type="button"
            onClick={() => setTypeFilter(f.key)}
            className={`rounded-full px-3.5 py-1.5 font-label-md text-[11.5px] font-semibold ${
              typeFilter === f.key
                ? "bg-on-background text-inverse-on-surface dark:bg-inverse-on-surface dark:text-inverse-surface"
                : "border border-outline-variant/60 bg-surface-container-lowest text-on-surface-variant dark:border-outline/40 dark:bg-inverse-surface dark:text-secondary-fixed-dim"
            }`}
          >
            {f.label} {f.count}
          </button>
        ))}
        {typeCounts.erkannt > 0 && (
          <button
            type="button"
            data-testid="filter-kundentyp-erkannt"
            aria-pressed={nurErkannt}
            onClick={() => setzeZustand({ erkannt: nurErkannt ? "" : "1" })}
            className={`inline-flex items-center gap-1 rounded-full px-3.5 py-1.5 font-label-md text-[11.5px] font-semibold ${
              nurErkannt
                ? "bg-on-background text-inverse-on-surface dark:bg-inverse-on-surface dark:text-inverse-surface"
                : "border border-outline-variant/60 bg-surface-container-lowest text-on-surface-variant dark:border-outline/40 dark:bg-inverse-surface dark:text-secondary-fixed-dim"
            }`}
          >
            <Icon name="auto_awesome" className="text-[13px]" /> automatisch erkannt {typeCounts.erkannt}
          </button>
        )}
      </div>
      )}

      <Card className="overflow-hidden">
        {narrow && (
          <ListenReiter
            label="Kunden nach Typ"
            reiter={[
              { key: "all" as const, label: "Alle", anzahl: suchTreffer.length },
              { key: "privat" as const, label: "Privat", anzahl: typeCounts.privat },
              { key: "firma" as const, label: "Firmen", anzahl: typeCounts.firma },
              { key: "verein" as const, label: "Vereine", anzahl: typeCounts.verein },
            ]}
            aktiv={typeFilter}
            onWahl={setTypeFilter}
          />
        )}
        {/* Suchleiste */}
        <div className="flex flex-wrap items-center gap-3 border-b border-outline-variant/30 p-4 dark:border-outline/30">
          <div className="group relative min-w-0 max-w-sm flex-1">
            <Icon
              name="search"
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[18px] text-on-surface-variant transition-colors group-focus-within:text-primary dark:text-secondary-fixed-dim"
            />
            <Input
              className="!pl-10"
              placeholder={narrow ? "Name, Firma, E-Mail …" : "Suchen (Name, Firma, E-Mail) …"}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          {narrow && (
            <SortierMenue optionen={KUNDEN_SORTIERUNG} sortKey={sort.sortKey} dir={sort.dir} onWahl={sort.setze} className="[&>div]:min-w-[130px]" />
          )}
        </div>

        {error ? (
          <ErrorNote zentriert title="Kunden konnten nicht geladen werden" message={error} onRetry={reload} />
        ) : !loading && customers.length === 0 ? (
          <EmptyState
            icon="group"
            title="Noch keine Kunden"
            hint="Sobald eine Anfrage eingeht oder du einen Kunden anlegst, erscheint er hier."
            cta={{ label: "Kunde anlegen", icon: "person_add", onClick: () => setCreateOpen(true) }}
          />
        ) : !loading && filtered.length === 0 ? (
          // Punkt 115 (G1): Treffer unter anderen Typen nennen; „Suche aufheben“ getrennt.
          <EmptyState
            icon={search.trim() ? "search_off" : "group"}
            title={search.trim() && suchTreffer.length === 0 ? `Kein Kunde passt zu „${search.trim()}“` : "Keine Kunden gefunden"}
            hint={search.trim() && suchTreffer.length === 0 ? "Andere Suche versuchen oder in allen Bereichen suchen." : "Wechsle den Filter oder zeige alle Kunden an."}
            andereTreffer={(
              [
                { key: "privat", label: "Privat" },
                { key: "firma", label: "Firmen" },
                { key: "verein", label: "Vereine" },
              ] as const
            )
              .filter((t) => t.key !== typeFilter)
              .map((t) => ({ label: t.label, anzahl: typeCounts[t.key], onClick: () => setTypeFilter(t.key) }))}
            link={[
              ...(typeFilter !== "all" && suchTreffer.length > 0 ? [{ label: "Alle Kunden anzeigen", onClick: () => setTypeFilter("all") }] : []),
              ...(search.trim() ? [{ label: "In allen Bereichen suchen", onClick: () => oeffneGlobaleSuche(search.trim()) }] : []),
              ...(search.trim() ? [{ label: "Suche aufheben", onClick: () => setSearch("") }] : []),
            ]}
          />
        ) : narrow ? (
          loading ? (
            <LoadingRows rows={6} />
          ) : (
            <KundenKarten zeilen={sortiert} />
          )
        ) : (
          // Listenstandard (Brief § 2.4): eigener Scrollbereich, damit der Kopf `sticky` stehen
          // bleibt (an der Karte mit `overflow-hidden` scheitert `sticky` still).
          <div className="max-h-[max(20rem,calc(100vh-19rem))] overflow-auto" aria-busy={loading}>
            {/* H1: 7 Spalten (Firma/Typ, E-Mail, Telefon getrennt + Letzter Kontakt) */}
            <table className="w-full min-w-[980px] table-fixed border-collapse text-left text-[13px]">
              <colgroup>
                <col />
                <col />
                <col />
                <col className="w-[150px]" />
                <col className="w-[92px]" />
                <col className="w-[150px]" />
                <col className="w-[160px]" />
              </colgroup>
              <thead>
                <tr>
                  {KUNDEN_SPALTEN.map((sp) => (
                    <SortierKopf
                      key={sp.key}
                      label={sp.label}
                      aktiv={sort.sortKey === sp.key ? sort.dir : null}
                      onClick={() => sort.toggle(sp.key)}
                      rechts={RECHTS.has(sp.key)}
                    />
                  ))}
                </tr>
              </thead>
              {loading ? (
                <LoadingRows rows={8} spalten={["text", "text", "text", "text", "text", "text", "text"]} />
              ) : (
                <tbody>
                  {sortiert.map(({ c, events: anzahlEvents, revenue, openCents, isMerged, isLost, lastContact, type }) => (
                    // Einheit E (`docs/PLAN-VORGANGSSEITE-UMBAU.md`, 13.09.2026): die Zeile war
                    // ein `onClick` ohne Linkziel — Cmd-/Strg-/Mittelklick und „Link in neuem Tab
                    // öffnen“ gingen deshalb ins Leere. Jetzt deckt ein echter `<Link>` die Zeile
                    // ab (`ZeilenLink`), `relative` auf dem `<tr>` ist sein Bezugsrahmen.
                    <tr
                      key={c.id}
                      className={`group relative transition-colors hover:bg-[#FBF6EC] dark:hover:bg-white/[0.03] ${isLost ? "opacity-55" : ""}`}
                    >
                      <td className={ZELLE}>
                        <ZeilenLink to={`/kunden/${c.id}`} label={`Kundenprofil von ${c.name} öffnen`} />
                        <div className="flex min-w-0 items-center gap-3">
                          <div
                            className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full font-label-md text-[12px] font-bold ${
                              isLost ? "bg-surface-variant text-secondary" : "bg-primary/14 text-primary"
                            }`}
                          >
                            {initials(c.name)}
                          </div>
                          <div className="min-w-0">
                            <span
                              title={c.name}
                              className="block truncate font-semibold text-on-background transition-colors group-hover:text-primary dark:text-inverse-on-surface"
                            >
                              {c.name}
                            </span>
                            {isMerged && (
                              <span className="mt-0.5 inline-flex items-center gap-1 rounded-full bg-surface-variant px-2 py-0.5 font-label-md text-[10px] font-semibold text-secondary dark:bg-white/[0.08] dark:text-secondary-fixed-dim">
                                <Icon name="merge" className="text-[12px]" /> Zusammengeführt
                              </span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className={`${ZELLE} text-on-background dark:text-inverse-on-surface`}>
                        {c.company ? (
                          <span className="block truncate" title={c.company}>
                            {c.company}
                          </span>
                        ) : (
                          <span className="text-secondary-fixed-dim">{type === "privat" ? "Privat" : <KeineAngabe />}</span>
                        )}
                      </td>
                      <td className={`${ZELLE} text-[#57503F] dark:text-secondary-fixed-dim`}>
                        {c.email ? (
                          <span className="block truncate" title={c.email}>
                            {c.email}
                          </span>
                        ) : (
                          <KeineAngabe />
                        )}
                      </td>
                      <td className={`${ZELLE} whitespace-nowrap tabular-nums text-[#57503F] dark:text-secondary-fixed-dim`}>
                        {c.phone || <KeineAngabe />}
                      </td>
                      <td className={`${ZELLE} text-right tabular-nums text-on-surface-variant dark:text-secondary-fixed-dim`}>{anzahlEvents}</td>
                      <td className={`${ZELLE} text-right font-semibold tabular-nums`}>
                        {/* Antoine 23.09.2026: beides zeigen — bezahlt als Zahl (danach wird
                            sortiert), offene Beträge klein darunter. */}
                        {revenue > 0 ? (
                          <span className="block text-on-background dark:text-inverse-on-surface">{money(revenue)}</span>
                        ) : openCents === 0 ? (
                          <KeineAngabe />
                        ) : null}
                        {openCents > 0 && (
                          <span className="block text-[11px] font-medium text-[#6F675A] dark:text-[#b8ae9e]">{money(openCents)} offen</span>
                        )}
                      </td>
                      <td className={`${ZELLE} whitespace-nowrap text-right tabular-nums text-on-surface-variant dark:text-secondary-fixed-dim`}>
                        {formatDate(lastContact)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              )}
            </table>
          </div>
        )}
        {!error && (
          <ListFooter
            anzahl={filtered.length}
            gesamt={customers.length}
            einheit={customers.length === 1 ? "Kunde" : "Kunden"}
            summe={money(sichtbarerUmsatzCents)}
            zusatz={
              topCustomer && topCustomer.revenue > 0 ? (
                <>
                  Top-Kunde: {topCustomer.name} · {money(topCustomer.revenue)}
                </>
              ) : undefined
            }
            sortiertNach={sort.spalte?.label ?? null}
            richtung={sort.dir}
            onReset={sort.reset}
            ohneSortierHinweis={narrow}
          />
        )}
      </Card>
      </>
      )}

      {/* Anlegen */}
      <Drawer open={createOpen} onClose={() => setCreateOpen(false)} title="Kunde anlegen">
        <form className="space-y-4" onSubmit={(e) => void handleCreate(e)}>
          {formError && <ErrorNote message={formError} />}
          <label className="block">
            <span className="mb-1.5 block font-body-sm text-body-sm font-semibold text-on-background dark:text-inverse-on-surface">
              Name *
            </span>
            <Input name="name" required />
          </label>
          <label className="block">
            <span className="mb-1.5 block font-body-sm text-body-sm font-semibold text-on-background dark:text-inverse-on-surface">
              E-Mail *
            </span>
            <Input name="email" type="email" required />
          </label>
          <label className="block">
            <span className="mb-1.5 block font-body-sm text-body-sm font-semibold text-on-background dark:text-inverse-on-surface">
              Firma
            </span>
            <Input name="company" />
          </label>
          <label className="block">
            <span className="mb-1.5 block font-body-sm text-body-sm font-semibold text-on-background dark:text-inverse-on-surface">
              Telefon
            </span>
            <Input name="phone" />
          </label>
          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="secondary" onClick={() => setCreateOpen(false)}>
              Abbrechen
            </Button>
            <Button type="submit" variant="primary" disabled={busy}>
              {busy ? "Speichert …" : "Kunde anlegen"}
            </Button>
          </div>
        </form>
      </Drawer>
    </>
  );
}

/* ══════════════════════════════════════════════════════════════════════════════════════════════
 * Mehrfachauswahl-Merge (Antoine 31.08.2026) — EIN Bestätigungsdialog für alle ausgewählten
 * Dubletten-Paare.
 * ════════════════════════════════════════════════════════════════════════════════════════════ */

/** Wo im Ablauf ein Paar gerade steht — für die Zeilen-Anzeige während/nach dem Lauf. */
type BulkMergeStatus = "wartet" | "laeuft" | "ok" | "fehler";
interface BulkMergeErgebnis {
  status: BulkMergeStatus;
  fehler?: string;
}

/**
 * DER Bestätigungsdialog für die Mehrfachauswahl. Listet ALLE ausgewählten Paare kompakt auf —
 * dieselbe a=Ziel(„bleibt“)/b=Quelle(„wird zusammengeführt“)-Konvention wie die Dubletten-Liste
 * und der bestehende Einzel-Merge-Flow (customer-detail.tsx `KundeZusammenfuehren`), NICHT neu
 * erfunden.
 *
 * Backend-Architektur-Entscheidung: KEIN neuer Batch-Endpunkt. Die bestehende, bereits getestete
 * Merge-Logik (`POST /api/customers/:id/merge`: interne Notizen, Merge-Ketten-Verfolgung via
 * `lib/kunden-merge.ts`, Grabstein, activity_log) wird NICHT dupliziert — stattdessen ruft diese
 * Komponente den bestehenden Endpunkt SEQUENZIELL je Paar auf (nicht parallel: mehrere
 * gleichzeitige Merges könnten sich bei überlappenden Kunden/Merge-Ketten in die Quere kommen,
 * z. B. wenn zwei ausgewählte Paare denselben Kunden teilen). Ein fehlgeschlagenes Paar bricht
 * die anderen NICHT ab — der Lauf geht weiter, am Ende steht eine Zusammenfassung.
 */
function BulkZusammenfuehrenDialog({
  offen,
  paare,
  onClose,
  onFertig,
}: {
  offen: boolean;
  /** Die aktuell in der Liste ausgewählten Paare — vom Aufrufer bei JEDEM Render frisch aus
   *  `dupQ.data` gefiltert. Der Dialog übernimmt sie nur beim ÖFFNEN in einen eigenen Schnappschuss
   *  (siehe `paareSnapshot` unten): `onFertig` räumt die Auswahl im selben Tick auf, in dem der
   *  letzte Merge durchläuft — ohne Schnappschuss würde die dadurch leere `paare`-Prop dem noch
   *  offenen "fertig"-Bildschirm die gerade erst gezeigte Erfolgs-/Fehler-Liste unter den Füßen
   *  wegziehen, bevor der Betreiber sie lesen kann. */
  paare: DuplicatePair[];
  onClose: () => void;
  /** Nach Abschluss des gesamten Laufs (Erfolge + Fehlschläge): Auswahl aufräumen + Dubletten-
   *  Liste neu laden. */
  onFertig: () => void;
}) {
  const [status, setStatus] = useState<"bestaetigen" | "laeuft" | "fertig">("bestaetigen");
  const [ergebnisse, setErgebnisse] = useState<Record<string, BulkMergeErgebnis>>({});
  const [paareSnapshot, setPaareSnapshot] = useState<DuplicatePair[]>([]);

  // Schnappschuss + sauberer Start bei jedem (erneuten) Öffnen — ein liegen gebliebenes Ergebnis
  // vom letzten Lauf darf den neuen nicht vorbelegen (gleiches Muster wie KundeZusammenfuehren),
  // UND die zu bearbeitende Liste steht fest, sobald der Betreiber sie bestätigt sieht.
  useEffect(() => {
    if (offen) {
      setStatus("bestaetigen");
      setErgebnisse({});
      setPaareSnapshot(paare);
    }
    // `paare` bewusst NICHT in den Deps: der Schnappschuss soll ausschließlich beim Übergang
    // geschlossen->offen neu gezogen werden, nicht bei jeder Änderung der Live-Auswahl während
    // der Dialog schon offen ist.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offen]);

  const erledigteAnzahl = paareSnapshot.filter((p) => {
    const s = ergebnisse[dupKey(p)]?.status;
    return s === "ok" || s === "fehler";
  }).length;
  const erfolgeAnzahl = paareSnapshot.filter((p) => ergebnisse[dupKey(p)]?.status === "ok").length;
  const fehlgeschlagen = paareSnapshot.filter((p) => ergebnisse[dupKey(p)]?.status === "fehler");

  async function starten() {
    setStatus("laeuft");
    // Sequenziell mit `for…of` + `await` (NICHT Promise.all/parallel) — Antoines Vorgabe
    // 31.08.2026, siehe Kopfkommentar. Jedes Paar bekommt sein eigenes Ergebnis, unabhängig
    // vom Ausgang der anderen.
    for (const p of paareSnapshot) {
      const k = dupKey(p);
      setErgebnisse((prev) => ({ ...prev, [k]: { status: "laeuft" } }));
      try {
        await apiMutate(`/api/customers/${p.a.id}/merge`, "POST", { sourceCustomerId: p.b.id, confirm: true });
        setErgebnisse((prev) => ({ ...prev, [k]: { status: "ok" } }));
      } catch (err) {
        setErgebnisse((prev) => ({ ...prev, [k]: { status: "fehler", fehler: err instanceof Error ? err.message : String(err) } }));
      }
    }
    setStatus("fertig");
    onFertig();
  }

  return (
    <Modal
      open={offen}
      onClose={() => status !== "laeuft" && onClose()}
      width="640px"
      testId="bulk-merge-dialog"
      title={paareSnapshot.length === 1 ? "1 Kundenpaar zusammenführen" : `${paareSnapshot.length} Kundenpaare zusammenführen`}
      subtitle={
        <>
          Der jeweils erste Kunde bleibt, der zweite wird zum Verweis (Grabstein) und verschwindet aus der aktiven Liste.{" "}
          <strong>Dieser Schritt kann nicht rückgängig gemacht werden.</strong>
        </>
      }
      footer={
        <div className="w-full">
          {status === "laeuft" && (
            <p className="mb-3 font-body-sm text-[12.5px] text-secondary dark:text-secondary-fixed-dim">
              {erledigteAnzahl} von {paareSnapshot.length} zusammengeführt …
            </p>
          )}
          {status === "fertig" && (
            <p data-testid="bulk-merge-bericht" className="mb-3 font-body-sm text-[12.5px] font-semibold text-on-background dark:text-inverse-on-surface">
              {erfolgeAnzahl} von {paareSnapshot.length} erfolgreich
              {fehlgeschlagen.length > 0
                ? `, ${fehlgeschlagen.length} fehlgeschlagen: ${fehlgeschlagen.map((p) => `„${p.b.name}“ → „${p.a.name}“: ${ergebnisse[dupKey(p)]?.fehler ?? "unbekannter Fehler"}`).join("; ")}`
                : "."}
            </p>
          )}
          <div className="flex justify-end gap-2.5">
            {status === "bestaetigen" && (
              <>
                <Button type="button" variant="secondary" onClick={onClose}>
                  Abbrechen
                </Button>
                <Button type="button" variant="primary" disabled={paareSnapshot.length === 0} onClick={() => void starten()}>
                  <Icon name="merge" className="text-[16px]" /> Jetzt zusammenführen
                </Button>
              </>
            )}
            {status === "laeuft" && (
              <Button type="button" variant="primary" disabled>
                <Icon name="progress_activity" className="animate-spin text-[16px]" /> Läuft …
              </Button>
            )}
            {status === "fertig" && (
              <Button type="button" variant="primary" onClick={onClose}>
                Schließen
              </Button>
            )}
          </div>
        </div>
      }
    >
      <div>
          <ul className="space-y-2">
            {paareSnapshot.map((p) => {
              const k = dupKey(p);
              const r = ergebnisse[k];
              return (
                <li
                  key={k}
                  data-testid={`bulk-merge-paar-${k}`}
                  className={`rounded-lg border px-3 py-2.5 text-[12.5px] ${
                    r?.status === "fehler"
                      ? "border-error/40 bg-error/5"
                      : r?.status === "ok"
                        ? "border-primary/30 bg-primary/5"
                        : "border-outline-variant/40 dark:border-outline/30"
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span className="min-w-0 flex-1 truncate">
                      <span className="font-semibold text-primary">Bleibt:</span>{" "}
                      <span className="font-semibold text-on-background dark:text-inverse-on-surface">{p.a.name}</span>
                    </span>
                    <Icon name="sync_alt" className="shrink-0 text-[14px] text-on-surface-variant/60" />
                    <span className="min-w-0 flex-1 truncate">
                      <span className="text-secondary-fixed-dim">Wird zusammengeführt:</span>{" "}
                      <span className="font-semibold text-on-background dark:text-inverse-on-surface">{p.b.name}</span>
                    </span>
                    {r?.status === "laeuft" && <Icon name="progress_activity" className="shrink-0 animate-spin text-[16px] text-primary" />}
                    {r?.status === "ok" && <Icon name="check_circle" className="shrink-0 text-[16px] text-tertiary dark:text-tertiary-fixed-dim" />}
                    {r?.status === "fehler" && <Icon name="error" className="shrink-0 text-[16px] text-error" />}
                  </div>
                  {r?.status === "fehler" && <p className="mt-1 font-body-sm text-[11.5px] text-error">{r.fehler}</p>}
                </li>
              );
            })}
          </ul>
      </div>
    </Modal>
  );
}

/**
 * Handy-Mockup 26.09.2026: Karte je Kunde — Firma bzw. Name, darunter Ansprechpartner oder Kontakt
 * und Typ, dann Events · Umsatz · letzter Kontakt. Rechts ein Anruf-Knopf (`tel:`), getrennt vom
 * Link auf das Kundenprofil (kein Link im Link).
 */
function KundenKarten({ zeilen }: { zeilen: KundenZeile[] }) {
  return (
    <ul data-testid="kunden-karten">
      {zeilen.map(({ c, events, revenue, openCents, isLost, lastContact, type }) => (
        <li key={c.id} className={`flex items-center border-t border-[#F6F0E4] first:border-t-0 dark:border-white/[0.06] ${isLost ? "opacity-55" : ""}`}>
          <Link to={`/kunden/${c.id}`} data-testid={`kunden-karte-${c.id}`} className="flex min-w-0 flex-1 items-start gap-3 py-3 pl-4 pr-2">
            <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-[12px] font-bold ${isLost ? "bg-surface-variant text-secondary" : "bg-primary/14 text-primary"}`}>
              {initials(c.company || c.name)}
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-[13.5px] font-semibold text-on-background dark:text-inverse-on-surface">{c.company || c.name}</span>
              <span className="block truncate text-[11.5px] text-secondary dark:text-secondary-fixed-dim">
                {[c.company ? c.name : c.email || c.phone, TYP_LABEL[type]].filter(Boolean).join(" · ")}
              </span>
              <span className="mt-0.5 block text-[11.5px] tabular-nums text-on-surface-variant dark:text-secondary-fixed-dim">
                {events} {events === 1 ? "Event" : "Events"}
                {revenue > 0 ? ` · ${money(revenue)}` : openCents > 0 ? ` · nur offen: ${money(openCents)}` : ""}
                {` · ${formatDate(lastContact)}`}
              </span>
            </span>
          </Link>
          {c.phone && (
            <a
              href={`tel:${c.phone.replace(/[^\d+]/g, "")}`}
              aria-label={`${c.name} anrufen`}
              className="mr-3 grid h-9 w-9 shrink-0 place-items-center rounded-full border border-outline-variant/60 text-on-surface-variant hover:text-primary dark:border-outline/40 dark:text-secondary-fixed-dim"
            >
              <Icon name="call" className="text-[18px]" />
            </a>
          )}
        </li>
      ))}
    </ul>
  );
}
